import { CSS } from '@dnd-kit/utilities'
import { DndContext } from '@dnd-kit/core'
import type { DragEndEvent, UniqueIdentifier } from '@dnd-kit/core'
import { Field, FieldArray, Form, Formik, useField } from 'formik'
import type { FormikConfig } from 'formik'
import type { ReactElement } from 'react'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { fromJS } from 'immutable'
import { v4 as generateUUID } from 'uuid'
import { omit } from 'lodash-es'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { useRef, useState } from 'react'

import { FormikOnChange } from '../product/ProductSettings'
import { Popover } from '../../../../Popover'
import { RichTextField } from '../../../../formComponents/RichTextField'
import {
  ScrollIntoViewAfterDropped,
  useSortableA11y,
  useSortableSensors,
} from '../../../../../utils/hooks/useSortableHelpers'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import FormGroup from '../../../../formComponents/FormikGroup'
import FormikValue from '../../../../formComponents/FormikValue'

type ExpanderPluginData = {
  entries: {
    title: string
    detailsHtml: string
    draggableId?: string // not stored anywhere, only required for dnd-kit
  }[]
}

const emptyEntry = {
  title: '',
  detailsHtml: '',
}

type Props = WorkspacePluginSettingsProps

export default function ExpanderSettings({ data, onCancel, onDataChange, onSave, renderLayout }: Props): ReactElement {
  const t = useTranslate('interface', 'components.expanderElementComponent.settingsModal')

  const formData = data.toJS() as ExpanderPluginData

  if (formData.entries.length === 0) formData.entries.push(emptyEntry)
  formData.entries.forEach((entry) => {
    entry.draggableId = generateUUID()
  })

  const title = t('title')

  const body = <SettingsLayerBody t={t} />

  const footer = (
    <ButtonRow>
      <Button variant="cancel" type="button" onClick={onCancel}>
        {t('cancelButton.label')}
      </Button>
      <Button type="submit">{t('saveButton.label')}</Button>
    </ButtonRow>
  )

  const handleSubmit: FormikConfig<ExpanderPluginData>['onSubmit'] = (values) => {
    return onSave(fromJS({ entries: values.entries.map((entry) => omit(entry, ['draggableId'])) }))
  }
  const handleChange = (values: ExpanderPluginData) => onDataChange(fromJS(values))

  return (
    <Formik<ExpanderPluginData> initialValues={formData} onSubmit={handleSubmit}>
      <Form translate="no">
        <FormikOnChange<ExpanderPluginData> onChange={handleChange} />
        {renderLayout({ title, body, footer })}
      </Form>
    </Formik>
  )
}

function SettingsLayerBody({ t }: TranslateProps) {
  const itemRefs = useRef(new Map()).current
  const [openedPopoverId, setOpenedPopoverId] = useState<number | null>(null)
  const closePopover = () => {
    const currentRef = itemRefs.get(openedPopoverId)
    currentRef?.focus()
    setOpenedPopoverId(null)
  }

  const [{ value: entries }] = useField<ExpanderPluginData['entries']>('entries')

  const sensors = useSortableSensors()

  const getTitle = (id: UniqueIdentifier) => {
    return entries.find((entry) => entry.draggableId === id)?.title || t('expandableEntry.headlinePlaceholder')
  }
  const a11y = useSortableA11y(t, getTitle)

  return (
    <FieldArray name="entries">
      {(fieldArrayHelpers) => {
        function handleDragEnd({ active, over }: DragEndEvent) {
          if (!over || over.id === active.id) return
          const oldIndex = entries.findIndex((entry) => entry.draggableId === active.id)
          const newIndex = entries.findIndex((entry) => entry.draggableId === over.id)
          fieldArrayHelpers.move(oldIndex, newIndex)
        }

        return (
          <div className="dali-settingslayer-main-content" tabIndex={-1}>
            <scroll-shadow>
              <DndContext
                accessibility={a11y}
                modifiers={[restrictToFirstScrollableAncestor, restrictToVerticalAxis]}
                sensors={sensors}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  items={entries.map((entry) => entry.draggableId!)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="scroller">
                    {entries.map((entry, index) => (
                      <div
                        ref={(node) => {
                          if (node) {
                            itemRefs.set(index, node)
                          } else {
                            itemRefs.delete(index)
                          }
                        }}
                        tabIndex={-1}
                        key={entry.draggableId}
                      >
                        <ExpandableEntry
                          t={t}
                          index={index}
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          id={entry.draggableId!}
                          onDeleteButtonClick={() => fieldArrayHelpers.remove(index)}
                          onEditButtonClick={() => setOpenedPopoverId(index)}
                        />
                        <Popover
                          open={openedPopoverId === index}
                          virtualRef={{ current: itemRefs.get(index) }}
                          side="left"
                          align="center"
                          className="dali-settingslayer-expander-popover"
                          onEscapeKeyDown={closePopover}
                          onPointerDownOutside={closePopover}
                          avoidCollisions
                          usePortal
                        >
                          <FormGroup
                            tagName="label"
                            label={t('expandableEntry.editPopover.headlineInputField.label')}
                            name={`entries.${index}.title`}
                          >
                            <Field
                              name={`entries.${index}.title`}
                              className="ep-form-text-field"
                              type="text"
                              placeholder={t('expandableEntry.editPopover.headlineInputField.placeholder')}
                              required
                            />
                          </FormGroup>
                          <FormGroup
                            className="dali-settingslayer-expander-popover-editor-wrapper"
                            label={t('expandableEntry.editPopover.textInputField.label')}
                            name={`entries.${index}.detailsHtml`}
                          >
                            <FormikValue name={`entries.${index}.detailsHtml`}>
                              {(value, setValue) => (
                                <RichTextField
                                  value={value}
                                  onChange={setValue}
                                  toolbarButtons={['bold', 'link|unlink']}
                                  placeholderText={t('expandableEntry.editPopover.textInputField.placeholder')}
                                />
                              )}
                            </FormikValue>
                          </FormGroup>
                        </Popover>
                      </div>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </scroll-shadow>
            <button
              type="button"
              onClick={() => fieldArrayHelpers.push({ ...emptyEntry, draggableId: generateUUID() })}
              className="dali-settingslayer-expander-add"
            >
              {t('addButton.label')}
            </button>
          </div>
        )
      }}
    </FieldArray>
  )
}

function ExpandableEntry({
  t,
  id,
  index,
  onEditButtonClick = () => null,
  onDeleteButtonClick = () => null,
}: Readonly<ExpandableEntryProps & { index: number; id: string }>): ReactElement {
  const [{ value: title }] = useField(`entries.${index}.title`)
  const [{ value: detailsHtml }] = useField(`entries.${index}.detailsHtml`)

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    attributes: { roleDescription: t('expandableEntry.ariaRoleDescription') },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="dali-settingslayer-expander-item"
      id={id}
    >
      <ScrollIntoViewAfterDropped id={id} />
      <div className="dali-settingslayer-expander-item-text">
        <span className="dali-settingslayer-expander-item-headline">
          {title || <i>{t('expandableEntry.headlinePlaceholder')}</i>}
        </span>
        <span
          className="htmlPreview"
          dangerouslySetInnerHTML={{
            __html: ['<p></p>', ''].includes(detailsHtml)
              ? `<i>${t('expandableEntry.textPlaceholder')}</i>`
              : detailsHtml,
          }}
        />
      </div>
      <button
        // https://github.com/clauderic/dnd-kit/issues/913
        onKeyDown={(e) => e.stopPropagation()}
        type="button"
        aria-label={t('expandableEntry.editButton.tooltip')}
        title={t('expandableEntry.editButton.tooltip')}
        onClick={onEditButtonClick}
        className="dali-settingslayer-expander-edit-button"
      ></button>
      <button
        // https://github.com/clauderic/dnd-kit/issues/913
        onKeyDown={(e) => e.stopPropagation()}
        type="button"
        aria-label={t('expandableEntry.deleteButton.tooltip')}
        title={t('expandableEntry.deleteButton.tooltip')}
        onClick={onDeleteButtonClick}
        className="dali-settingslayer-expander-delete-button"
      ></button>
    </div>
  )
}

type ExpandableEntryProps = {
  onEditButtonClick?: () => void
  onDeleteButtonClick?: () => void
} & TranslateProps
